import type { ThirdPartyDeviceType } from "verdi-common";
import { DeviceTypeConfigurations, getVerdiConfiguration } from "verdi-common";
import { type DeviceConfiguration } from "verdi-common/Configurations/DeviceConfiguration";
import { MasterIndex } from "verdi-web-client";
import type { Zone } from "verdi-web-client/Models";
import { findGetters } from "verdi-common/schemas/utils/findGetters";
import type { ThirdPartyDevice } from "verdi-common/schemas/thirdPartyDevice/thirdPartyDevice";
import type { Device } from "verdi-common/schemas/device/device";
import { getDisplayId as _getDisplayId } from "verdi-common/schemas/commonGetters/getDisplayId";
import { getDisplayName as _getDisplayName } from "verdi-common/schemas/commonGetters/getDisplayName";
import type MapEntityBase from "../../map-management/map-entities/map-entity-base";

export function isSensorOnly({ model }: { model: MapEntityBase["model"] }) {
    const type = model.type as DeviceConfiguration["type"];
    const configuration = DeviceTypeConfigurations[type];
    // Third-party and unknown types are not considered sensor-only here
    return configuration?.isVerdiDevice === true && configuration.valveCount === 0;
}

/**
 * Get an array of connected zones for a device.
 * If the device has connectedZones, return them.
 * If the device has connectedZoneIDs, look up the zone for each
 * If the device has neither, return an empty array.
 */
export function getConnectedZones({ model }: { model: MapEntityBase["model"] }): Zone[] {
    return (
        model?.connectedZones ??
        model?.connectedZoneIDs?.flatMap((zid: string) => MasterIndex.zone.byID[zid] ?? []) ??
        []
    );
}

export function getDeviceType({ model }: { model: MapEntityBase["model"] }) {
    return model.type as DeviceConfiguration["type"];
}

/**
 * True if this device type is configured to stay visible on the map regardless of selected AOI/field (e.g. gateways).
 */
export function isVisibleRegardlessOfSelectedAOI({ model }: { model: MapEntityBase["model"] }): boolean {
    const deviceType = model?.deviceType ?? model?.type;
    if (!deviceType) return false;
    const config = getVerdiConfiguration(deviceType as DeviceConfiguration["type"] | ThirdPartyDeviceType);
    return config?.visibleRegardlessOfSelectedSite === true;
}

export function isValve({ deviceType }: { deviceType: DeviceConfiguration["type"] | ThirdPartyDeviceType }): boolean {
    // Unknown/legacy type strings and third-party devices are never valves
    const configuration = DeviceTypeConfigurations[deviceType];
    if (!configuration?.isVerdiDevice) {
        return false;
    }
    return configuration.valveType !== "none";
}

export function isSensor({ deviceType }: { deviceType: DeviceConfiguration["type"] }) {
    const configuration = DeviceTypeConfigurations[deviceType];
    return configuration?.isVerdiDevice === true && configuration.valveType === "none";
}

/**
 * Get the friendly name for a device
 * If the device's name is the same as its EUI, return the short code (eg. KEZ-283) as the friendly name
 * else return the device's user-defined name
 *
 * @deprecated - use the common getter directly instead if possible.
 */
export function getDisplayName({ model }: { model: MapEntityBase["model"] }) {
    if (!model) {
        return "Unknown";
    }

    try {
        const standardGetter = findGetters(model._lastJSON as ThirdPartyDevice | Device).getDisplayName;

        if (standardGetter) {
            return standardGetter();
        }
    } catch {
        // model may not be a registered verdi-common schema (e.g. SensoterraMoistureSensor)
    }

    return _getDisplayName(model._lastJSON);
}

/**
 * Cascading logic to get a displayable ID for a device.
 * 1. Check if the device has a valid EUI,
 *   convert it to a short code and return it.
 *   else, return the raw id
 * 2. Check if the device has an EnclosureID, return it if it does
 * 3. Return "Unknown"
 *
 * Splits out the ID into 3 parts:
 * 1. displayID: The ID that is displayed to the user
 * 2. eui: The EUI of the device
 * 3. adminId: The ID that is used for admin purposes, might be EUI, EnclosureID, or the raw id
 *
 * @deprecated - use the common getter directly instead if possible.
 */
export function getDisplayID({ model }: { model: MapEntityBase["model"] }) {
    if (!model) {
        return {
            displayID: "Unknown",
            eui: "Unknown",
            adminId: "Unknown",
        };
    }
    try {
        const standardGetter = findGetters(model._lastJSON as ThirdPartyDevice | Device).getDisplayId;

        if (standardGetter) {
            return standardGetter();
        }
    } catch {
        // model may not be a registered verdi-common schema (e.g. SensoterraMoistureSensor)
    }

    return _getDisplayId(model._lastJSON);
}

/**
 * If the device has multiple valves, each valve supports one zone (1:1 relationship)
 */
export const doesValveSupportMultipleConnectedZones = ({ valveCount }: { valveCount: number }) => valveCount === 1;

/**
 * Finds the connected zones for a valve.
 * If there is only one valve, that valve supports many connected zones (1:many relationship)
 * If there are multiple valves, each valve supports one zone (1:1 relationship)
 */
export const findConnectedZonesForValve = ({
    valveCount,
    connectedZones,
    valveIndex,
}: {
    valveCount: number;
    connectedZones: Zone[];
    valveIndex: number;
}) => {
    if (doesValveSupportMultipleConnectedZones({ valveCount })) {
        return connectedZones;
    }
    return connectedZones?.[valveIndex] ? [connectedZones[valveIndex]] : [];
};
